import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  Tooltip,
  Stack,
  Chip,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import CartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCart } from '../../context/CartContext';

interface CartProps {
  isOpen?: boolean;
  onClose?: () => void;
  showTrigger?: boolean;
}

const Cart: React.FC<CartProps> = ({ isOpen: externalIsOpen, onClose: externalOnClose, showTrigger = true }) => {
  const navigate = useNavigate();

  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getItemCount,
  } = useCart();

  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Determine if we are controlled or uncontrolled
  const isControlled = externalIsOpen !== undefined;
  const open = isControlled ? externalIsOpen : internalIsOpen;

  const handleOpen = () => {
    if (isControlled) {
      // If controlled, we can't open it ourselves, but we shouldn't be clicking the internal trigger if controlled usually
      // But if showTrigger is true, we might want to support it.
      // Ideally, parent handles opening.
    } else {
      setInternalIsOpen(true);
    }
  };

  const handleClose = () => {
    if (isControlled && externalOnClose) {
      externalOnClose();
    } else {
      setInternalIsOpen(false);
    }
  };

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity >= 1) {
      void updateQuantity(productId, newQuantity);
    }
  };

  const handleCheckout = async () => {
    handleClose();
    navigate('/checkout');
  };

  const estimatedShipping = cartItems.length > 0 ? 250 : 0;
  const orderTotal = getCartTotal() + estimatedShipping;

  const CartItemList = () => (
    <List>
      {cartItems.map((item) => (
        <ListItem
          key={item.id}
          sx={{
            py: 2,
            px: 0,
            alignItems: 'stretch',
            gap: 1.5,
          }}
        >
          <ListItemAvatar>
            <Avatar
              variant="rounded"
              src={item.image}
              alt={item.name}
              sx={{ width: 80, height: 80 }}
            />
          </ListItemAvatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <ListItemText
              primary={
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {item.name}
                </Typography>
              }
              secondary={
                <Box component="span">
                  <Typography variant="body2" color="text.secondary" component="span" display="block">
                    {item.seller.name} • {item.seller.location}
                  </Typography>
                  <Typography variant="body2" component="span" display="block">
                    KSh {item.price.toLocaleString()} per {item.unit}
                  </Typography>
                </Box>
              }
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
              {typeof item.stock === 'number' && item.stock > 0 && item.stock <= 10 && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`Only ${item.stock} left`}
                />
              )}
              {typeof item.stock === 'number' && item.stock > 10 && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label="In stock"
                />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={`Item total: KSh ${(item.price * item.quantity).toLocaleString()}`}
              />
            </Stack>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 999 }}>
              <IconButton
                size="small"
                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
              >
                <RemoveIcon />
              </IconButton>
              <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
              <IconButton
                size="small"
                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                disabled={typeof item.stock === 'number' && item.quantity >= item.stock}
              >
                <AddIcon />
              </IconButton>
            </Box>
            <IconButton
              size="small"
              color="error"
              onClick={() => removeFromCart(item.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </ListItem>
      ))}
    </List>
  );

  const EmptyCart = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
      }}
    >
      <ShoppingBagIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        Your cart is empty
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Add items to your cart to proceed with checkout
      </Typography>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleClose}
        startIcon={<ShoppingBagIcon />}
      >
        Continue Shopping
      </Button>
    </Box>
  );

  return (
    <>
      {showTrigger && !isControlled && (
        <Tooltip title="Shopping Cart">
          <IconButton color="inherit" onClick={handleOpen}>
            <Badge badgeContent={getItemCount()} color="error">
              <CartIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 400 } },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CartIcon sx={{ mr: 1 }} />
            <Box>
              <Typography variant="h6">
                Shopping Cart ({getItemCount()} items)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Secure checkout, seller visibility, and live cart updates
              </Typography>
            </Box>
            <IconButton
              sx={{ ml: 'auto' }}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {cartItems.length === 0 ? (
            <EmptyCart />
          ) : (
            <>
              <Box sx={{ maxHeight: 'calc(100vh - 290px)', overflowY: 'auto', pr: 0.5 }}>
                <CartItemList />
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 3,
                  position: 'sticky',
                  bottom: 0,
                  bgcolor: 'background.paper',
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Order Summary
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Subtotal</Typography>
                      <Typography variant="body2">
                        KSh {getCartTotal().toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Estimated shipping</Typography>
                      <Typography variant="body2">KSh {estimatedShipping.toLocaleString()}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Final delivery cost is confirmed at checkout based on location.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1">Estimated total</Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        KSh {orderTotal.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Chip size="small" color="success" label="Protected checkout" />
                    <Chip size="small" variant="outlined" label="Seller details attached" />
                  </Stack>

                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="inherit"
                      onClick={() => void clearCart()}
                    >
                      Clear Cart
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleClose}
                      startIcon={<ShoppingBagIcon />}
                    >
                      Shop More
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={handleCheckout}
                    >
                      Checkout
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Cart;
